import { ScreenshotValidationOptions } from '../../domain/configuration/config.interface.js';
import { WopeeError } from '@autonomous-testing/sdk';

const DOCS_URL = 'https://docs.wopee.io/playwright-visual-testing/';
const CMD_URL = 'https://cmd.wopee.io';

/*
Mirrors the mapping in EnvConfigAdapter. Kept here so the error can name the
environment variable the user actually has to set, rather than the internal
config field.
*/
const REQUIRED_OPTIONS = [
  {
    key: 'apiKey',
    env: 'WOPEE_API_KEY',
    where: `Project Settings → API Keys at ${CMD_URL}`,
    placeholder: '<your api key>',
  },
  {
    key: 'projectUuid',
    env: 'WOPEE_PROJECT_UUID',
    where: `Project Settings at ${CMD_URL}`,
    placeholder: '<your project uuid>',
  },
] as const;

/*
WOPEE_API_URL is deliberately not required — it has a working SaaS default in
the SDK schema, and requiring it would break every existing SaaS user.
*/

function isMissing(value: unknown): boolean {
  return value === undefined || value === null || String(value).trim() === '';
}

/**
 * Fails fast, before the SDK adapter is constructed, so the user sees which
 * variable to set instead of a validation dump from deeper in the stack.
 */
export function assertScreenshotValidationConfig(options?: ScreenshotValidationOptions): void {
  // Same expression Wopee uses to decide whether to build the adapter, so the
  // two can never disagree about whether Wopee is switched on.
  if (!options?.screenshotValidationEnabled) {
    return;
  }

  const missing = REQUIRED_OPTIONS.filter((option) => isMissing(options[option.key]));
  if (missing.length === 0) {
    return;
  }

  const width = Math.max(...missing.map((option) => option.env.length));
  const message = [
    'Wopee is not configured — missing required environment variables.',
    '',
    '  Missing:',
    ...missing.map((option) => `    • ${option.env.padEnd(width)}  — ${option.where}`),
    '',
    '  Set them in your shell, in a .env file next to your Playwright config, or as',
    '  CI secrets:',
    '',
    ...missing.map((option) => `    ${option.env}=${option.placeholder}`),
    '    WOPEE_API_URL=https://api.wopee.io   # only for self-hosted instances',
    '',
    `  Docs: ${DOCS_URL}`,
  ].join('\n');

  // Same code the SDK uses for its own configuration failures, so callers can
  // branch on error.code regardless of which layer rejected the config.
  throw new WopeeError(message, { code: 'WOPEE_CONFIG_INVALID' });
}
